import React, { useState } from 'react';
import { Form, InputGroup, Spinner } from 'react-bootstrap';
import { SwitchButton, SwitchButtonState } from '../SwitchButton/SwitchButton';
import {
    REPRESENTATIVE_ROLE_MAP,
    SelectableRepresentative
} from '../../models/customer-representative';
import { ApplicationSigner, SignerRepresentative, SignerType } from '../../models/customer-application';
import { isPersonCode, isEmailValidAndPresent, isPhoneNumber } from '../../helpers/validators';
import { capitalizeWords, handleTextInputNumberChange } from '../../helpers';
import { FileInput, isFileTooLarge, isFileTypeUnaccepted } from '../Forms/FileInput';
import AddressAutocomplete from '../AddressAutocomplete/AddressAutocomplete';
import CustomerApplicationService from '../../services/customer-application.service';
import { downloadFile } from '../../helpers/apiResponseHelper';

export interface AuthorizedSignerForm {
    name: string;
    idCode: string;
    email: string;
    phone: string;
    address: string;
    validityDate: string;
    powerOfAttorney?: File;
}

export const EMPTY_AUTHORIZED_SIGNER: AuthorizedSignerForm = {
    name: '',
    idCode: '',
    email: '',
    phone: '',
    address: '',
    validityDate: '',
    powerOfAttorney: undefined
};

export function toSignerType(signers?: ApplicationSigner[]): SignerType {
    return signers && signers.length > 0 ? signers[0].type : SignerType.REPRESENTATIVE;
}

export function toSelectedRepresentatives(signers?: ApplicationSigner[]): SignerRepresentative[] {
    return (signers ?? [])
        .filter(signer => signer.type === SignerType.REPRESENTATIVE)
        .map(signer => ({
            name: signer.name,
            idCode: signer.idCode,
            email: signer.email,
            phone: signer.phone
        }));
}

/**
 * A registry representative who is not the contact person has no details on the application, so their
 * email and phone must be collected here.
 */
export function isSignerRepresentativeValid(
    representative: SignerRepresentative,
    contactPersonCode?: string
): boolean {
    if (representative.idCode === contactPersonCode) return true;

    return isEmailValidAndPresent(representative.email) && isPhoneNumber(representative.phone ?? '');
}

export function areRepresentativeSignersValid(
    representatives: SignerRepresentative[],
    contactPersonCode?: string
): boolean {
    if (representatives.length === 0) return false;

    return representatives.every(
        representative => isSignerRepresentativeValid(representative, contactPersonCode)
    );
}

export function toAuthorizedSignerForm(signers?: ApplicationSigner[]): AuthorizedSignerForm {
    const existing = (signers ?? []).find(signer => signer.type === SignerType.AUTHORIZED_REPRESENTATIVE);
    if (!existing) return EMPTY_AUTHORIZED_SIGNER;

    return {
        name: existing.name ?? '',
        idCode: existing.idCode ?? '',
        email: existing.email ?? '',
        phone: existing.phone ?? '',
        address: existing.address ?? '',
        validityDate: existing.validityDate ?? '',
        // The stored file is not sent back, so there is nothing to re-select; its name is shown instead.
        powerOfAttorney: undefined
    };
}

interface SignerSectionProps {
    signerType: SignerType;
    onSignerTypeChange: (type: SignerType) => void;
    representatives: SelectableRepresentative[];
    isLoadingRepresentatives: boolean;
    /** The selected registry representatives, carrying contact details for non-contact signers. */
    selectedRepresentatives: SignerRepresentative[];
    onSelectedRepresentativesChange: (representatives: SignerRepresentative[]) => void;
    authorizedSigner: AuthorizedSignerForm;
    onAuthorizedSignerChange: (signer: AuthorizedSignerForm) => void;
    /** Personal code of the logged-in contact person, marked as "sina" in the list. */
    currentUserPersonalCode?: string;
    /** Name of a volikiri already stored on the application, shown when editing. */
    storedPowerOfAttorneyFileName?: string;
    applicationId?: number;
    customerId?: number;
    disabled?: boolean;
    validated: boolean;
}

export const MAX_POWER_OF_ATTORNEY_SIZE_BYTES = 5 * 1024 * 1024;

/** Kept in sync with the @FileType types allowed on the create application endpoint. */
export const POWER_OF_ATTORNEY_ACCEPTED_TYPES = '.pdf,.docx,.bdoc,.asice';

/**
 * @param storedPowerOfAttorneyFileName name of a volikiri already stored on the application. When present the
 *                                      user need not upload a new one, since the stored document is kept.
 */
export function isAuthorizedSignerValid(
    signer: AuthorizedSignerForm,
    storedPowerOfAttorneyFileName?: string
): boolean {
    if (!signer.name.trim()) return false;
    if (!isPersonCode(signer.idCode)) return false;
    if (!isEmailValidAndPresent(signer.email)) return false;
    if (!isPhoneNumber(signer.phone)) return false;
    if (!signer.address.trim()) return false;
    if (!signer.powerOfAttorney && !storedPowerOfAttorneyFileName) return false;
    if (isFileTooLarge(signer.powerOfAttorney, MAX_POWER_OF_ATTORNEY_SIZE_BYTES)) return false;
    if (isFileTypeUnaccepted(signer.powerOfAttorney, POWER_OF_ATTORNEY_ACCEPTED_TYPES)) return false;

    return true;
}

export function SignerSection(props: Readonly<SignerSectionProps>) {
    const {
        signerType,
        onSignerTypeChange,
        representatives,
        isLoadingRepresentatives,
        selectedRepresentatives,
        onSelectedRepresentativesChange,
        authorizedSigner,
        onAuthorizedSignerChange,
        currentUserPersonalCode,
        storedPowerOfAttorneyFileName,
        applicationId,
        customerId,
        disabled,
        validated
    } = props;

    const [isDownloading, setIsDownloading] = useState(false);

    const pickedPowerOfAttorney = authorizedSigner.powerOfAttorney;
    const canDownloadPowerOfAttorney = !!pickedPowerOfAttorney || !!storedPowerOfAttorneyFileName;

    const downloadPowerOfAttorney = async () => {
        if (pickedPowerOfAttorney) {
            downloadFile(
                pickedPowerOfAttorney.name,
                pickedPowerOfAttorney,
                window.URL.createObjectURL(pickedPowerOfAttorney)
            );
            return;
        }

        if (!applicationId || !customerId) return;

        setIsDownloading(true);
        try {
            await CustomerApplicationService.downloadPowerOfAttorney(applicationId, customerId);
        } finally {
            setIsDownloading(false);
        }
    };

    const isRepresentatives = signerType === SignerType.REPRESENTATIVE;

    const toggleRepresentative = (representative: SelectableRepresentative) => {
        if (selectedRepresentatives.some(selected => selected.idCode === representative.personalCode)) {
            onSelectedRepresentativesChange(
                selectedRepresentatives.filter(selected => selected.idCode !== representative.personalCode)
            );
        } else {
            onSelectedRepresentativesChange([
                ...selectedRepresentatives,
                { name: representative.name, idCode: representative.personalCode }
            ]);
        }
    };

    const updateRepresentative = (idCode: string, changes: Partial<SignerRepresentative>) => {
        onSelectedRepresentativesChange(
            selectedRepresentatives.map(selected =>
                selected.idCode === idCode ? { ...selected, ...changes } : selected
            )
        );
    };

    const updateSigner = (changes: Partial<AuthorizedSignerForm>) => {
        onAuthorizedSignerChange({ ...authorizedSigner, ...changes });
    };

    return (
        <>
            <h2 className="col-12">Lepingu sõlmijad</h2>
            <div className="col-12 mb-3">
                <div className="alert alert-info mb-3" role="alert">
                    Iga esindusõiguslik isik võib lepingu sõlmida üksinda — vali vähemalt üks.
                </div>
                <div className="d-flex mb-3">
                    <SwitchButton
                        className="d-flex"
                        labelLeft="Äriregistri esindajad"
                        labelRight="Volitatud esindaja"
                        onActiveChange={active => {
                            onSignerTypeChange(
                                active === SwitchButtonState.LEFT
                                    ? SignerType.REPRESENTATIVE
                                    : SignerType.AUTHORIZED_REPRESENTATIVE
                            );
                        }}
                        active={isRepresentatives ? SwitchButtonState.LEFT : SwitchButtonState.RIGHT}
                        size="md"
                    />
                </div>

                {isRepresentatives ? (
                    <>
                        {isLoadingRepresentatives && (
                            <div className="p-4 w-100 d-flex align-items-center justify-content-center">
                                <Spinner animation="grow" />
                                <span className="sr-only">Laen andmeid...</span>
                            </div>
                        )}
                        {!isLoadingRepresentatives && representatives.length === 0 && (
                            <div>
                                Äriregistrist ei leitud esindusõiguslikke isikuid. Palun lisa volitatud esindaja.
                            </div>
                        )}
                        {representatives.map(representative => {
                            const selected = selectedRepresentatives.find(
                                candidate => candidate.idCode === representative.personalCode
                            );
                            const isContact = representative.personalCode === currentUserPersonalCode;

                            return (
                                <div
                                    key={`${representative.role}-${representative.personalCode}`}
                                    className="border rounded p-2 mb-2"
                                >
                                    <div className="d-flex align-items-center flex-wrap">
                                        <Form.Check
                                            custom
                                            type="checkbox"
                                            id={`signer-${representative.role}-${representative.personalCode}`}
                                            className="mr-2"
                                            disabled={disabled}
                                            checked={!!selected}
                                            onChange={() => toggleRepresentative(representative)}
                                            label={<strong>{representative.name}</strong>}
                                        />
                                        <span className="mr-2">
                                            {REPRESENTATIVE_ROLE_MAP[representative.role]}
                                            {isContact && ' — sina (kontaktisik)'}
                                        </span>
                                    </div>
                                    {selected && isContact && (
                                        <div className="mt-2">
                                            Kontaktandmed võetakse taotluse esitaja andmetest.
                                        </div>
                                    )}
                                    {selected && !isContact && (
                                        <div className="row mt-2">
                                            <Form.Group
                                                controlId={`signer-email-${representative.personalCode}`}
                                                className="col-md-6 mb-0"
                                            >
                                                <Form.Label>E-post</Form.Label>
                                                <Form.Control
                                                    required
                                                    type="email"
                                                    placeholder="E-post"
                                                    value={selected.email ?? ''}
                                                    onChange={e => updateRepresentative(
                                                        representative.personalCode,
                                                        { email: e.target.value }
                                                    )}
                                                    disabled={disabled}
                                                    isInvalid={validated && !isEmailValidAndPresent(selected.email)}
                                                />
                                                <Form.Control.Feedback type="invalid">
                                                    {!selected.email ? 'Kohustuslik väli' : 'Vigane e-post'}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                            <Form.Group
                                                controlId={`signer-phone-${representative.personalCode}`}
                                                className="col-md-6 mb-0"
                                            >
                                                <Form.Label>Telefon</Form.Label>
                                                <InputGroup className="col-12 p-0">
                                                    <InputGroup.Prepend>
                                                        <InputGroup.Text>+372</InputGroup.Text>
                                                    </InputGroup.Prepend>
                                                    <Form.Control
                                                        required
                                                        type="tel"
                                                        placeholder="Telefon"
                                                        value={selected.phone ?? ''}
                                                        onChange={e => updateRepresentative(
                                                            representative.personalCode,
                                                            { phone: handleTextInputNumberChange(selected.phone ?? '', e.target.value) }
                                                        )}
                                                        disabled={disabled}
                                                        isInvalid={validated && !isPhoneNumber(selected.phone ?? '')}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {!selected.phone ? 'Kohustuslik väli' : 'Vigane telefoninumber'}
                                                    </Form.Control.Feedback>
                                                </InputGroup>
                                            </Form.Group>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {validated && selectedRepresentatives.length === 0 && (
                            <div className="d-block text-left invalid-feedback">
                                Vähemalt üks lepingu sõlmija on kohustuslik
                            </div>
                        )}
                    </>
                ) : (
                    <div className="row">
                        <Form.Group controlId={'form-signer-name'} className={'col-md-6 mb-3'}>
                            <Form.Label>Nimi</Form.Label>
                            <Form.Control
                                required
                                value={authorizedSigner.name}
                                onChange={e => updateSigner({ name: capitalizeWords(e.target.value) })}
                                disabled={disabled}
                                type="text"
                                placeholder="Volitatud esindaja nimi"
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId={'form-signer-idcode'} className={'col-md-6 mb-3'}>
                            <Form.Label>Isikukood</Form.Label>
                            <Form.Control
                                required
                                value={authorizedSigner.idCode}
                                onChange={e => updateSigner({ idCode: e.target.value })}
                                disabled={disabled}
                                type="text"
                                placeholder="Isikukood"
                                isInvalid={validated && !isPersonCode(authorizedSigner.idCode)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {!authorizedSigner.idCode ? 'Kohustuslik väli' : 'Vigane isikukood'}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId={'form-signer-email'} className={'col-md-6 mb-3'}>
                            <Form.Label>E-post</Form.Label>
                            <Form.Control
                                required
                                value={authorizedSigner.email}
                                onChange={e => updateSigner({ email: e.target.value })}
                                disabled={disabled}
                                type="email"
                                placeholder="E-post"
                                isInvalid={validated && !isEmailValidAndPresent(authorizedSigner.email)}
                            />
                            <Form.Control.Feedback type="invalid">
                                {!authorizedSigner.email ? 'Kohustuslik väli' : 'Vigane e-post'}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group controlId={'form-signer-phone'} className={'col-md-6 mb-3'}>
                            <Form.Label>Telefon</Form.Label>
                            <InputGroup className="col-12 p-0">
                                <InputGroup.Prepend>
                                    <InputGroup.Text>+372</InputGroup.Text>
                                </InputGroup.Prepend>
                                <Form.Control
                                    required
                                    value={authorizedSigner.phone}
                                    onChange={e => updateSigner({ phone: handleTextInputNumberChange(authorizedSigner.phone, e.target.value) })}
                                    disabled={disabled}
                                    type="tel"
                                    placeholder="Telefon"
                                    isInvalid={validated && !isPhoneNumber(authorizedSigner.phone)}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {!authorizedSigner.phone ? 'Kohustuslik väli' : 'Vigane telefoninumber'}
                                </Form.Control.Feedback>
                            </InputGroup>
                        </Form.Group>
                        <Form.Group controlId={'form-signer-address'} className={'col-12 mb-3'}>
                            <Form.Label>Aadress</Form.Label>
                            <AddressAutocomplete
                                address={authorizedSigner.address}
                                setAddress={address => updateSigner({
                                    address: address.fullAddress || address.address || ''
                                })}
                                disabled={!!disabled}
                            />
                            <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                        </Form.Group>
                        <div className={'col-md-6'}>
                            <FileInput
                                controlId={'form-signer-power-of-attorney'}
                                className={'mb-1'}
                                label="Volikiri (fail)"
                                accept={POWER_OF_ATTORNEY_ACCEPTED_TYPES}
                                maxSizeBytes={MAX_POWER_OF_ATTORNEY_SIZE_BYTES}
                                value={authorizedSigner.powerOfAttorney}
                                onChange={file => updateSigner({ powerOfAttorney: file })}
                                required={!storedPowerOfAttorneyFileName}
                                placeholder={storedPowerOfAttorneyFileName}
                                disabled={disabled}
                                validated={validated}
                            />
                            { canDownloadPowerOfAttorney && (
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm small p-0 mb-3 text-left"
                                    onClick={downloadPowerOfAttorney}
                                    disabled={isDownloading}
                                >
                                    Lae alla
                                </button>
                            )}
                        </div>
                        <Form.Group controlId={'form-signer-validity-date'} className={'col-md-6 mb-3'}>
                            <Form.Label>Volikirja kehtivusaeg</Form.Label>
                            <Form.Control
                                value={authorizedSigner.validityDate}
                                onChange={e => updateSigner({ validityDate: e.target.value })}
                                disabled={disabled}
                                type="date"
                            />
                            <Form.Text>Tühjaks jätmisel on volikiri tähtajatu</Form.Text>
                        </Form.Group>
                    </div>
                )}
            </div>
        </>
    );
}
